
import {
    Children,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";

const ResponsiveChildren = ({ children, minGap = 24, className = "" }) => {
    const containerRef = useRef(null);
    const itemRefs = useRef([]);
    const [containerWidth, setContainerWidth] = useState(0);
    const [itemWidths, setItemWidths] = useState([]);

    const items = Children.toArray(children);

    useLayoutEffect(() => {
        const container = containerRef.current;

        if (!container) {
            return;
        }

        const measure = () => {
            setContainerWidth(container.clientWidth);

            setItemWidths(
                itemRefs.current
                    .slice(0, items.length)
                    .map((item) => item?.getBoundingClientRect().width ?? 0)
            );
        };

        const observer = new ResizeObserver(measure);

        observer.observe(container);

        itemRefs.current.slice(0, items.length).forEach((item) => {
            if (item) {
                observer.observe(item);
            }
        });

        measure();

        return () => observer.disconnect();
    }, [items.length]);

    const rows = useMemo(() => {
        if (items.length === 0) {
            return [];
        }

        if (
            containerWidth <= 0 ||
            itemWidths.length !== items.length ||
            itemWidths.some((width) => width === 0)
        ) {
            return [items.map((_, index) => index)];
        }

        const totalWidth =
            itemWidths.reduce((total, width) => total + width, 0) +
            minGap * (items.length - 1);

        // If everything fits, use one left-aligned row.
        if (totalWidth <= containerWidth) {
            return [items.map((_, index) => index)];
        }

        // Otherwise, greedily fill each row while preserving minGap.
        const result = [];
        let currentRow = [];
        let currentWidth = 0;

        itemWidths.forEach((width, index) => {
            const requiredWidth =
                currentRow.length === 0
                    ? width
                    : currentWidth + minGap + width;

            if (
                currentRow.length > 0 &&
                requiredWidth > containerWidth
            ) {
                result.push(currentRow);
                currentRow = [index];
                currentWidth = width;
            } else {
                currentRow.push(index);
                currentWidth = requiredWidth;
            }
        });

        if (currentRow.length > 0) {
            result.push(currentRow);
        }

        return result;
    }, [items, itemWidths, containerWidth, minGap]);

    if (items.length <= 1) {
        return children ?? null;
    }

    return (
        <div
            ref={containerRef}
            className={className}
            style={{
                width: "100%",
                minWidth: 0,
            }}
        >
            {/* Measurement row: all items retain their natural widths. */}
            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    visibility: "hidden",
                    pointerEvents: "none",
                    height: 0,
                    overflow: "hidden",
                    display: "flex",
                    flexWrap: "nowrap",
                    gap: minGap,
                }}
            >
                {items.map((child, index) => (
                    <div
                        key={child.key ?? index}
                        ref={(element) => {
                            itemRefs.current[index] = element;
                        }}
                        style={{
                            flex: "0 0 auto",
                            width: "max-content",
                        }}
                    >
                        {child}
                    </div>
                ))}
            </div>

            {/* Actual rows */}
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: minGap,
                    width: "100%",
                }}
            >
                {rows.map((row, rowIndex) => (
                    <div
                        key={rowIndex}
                        style={{
                            display: "flex",
                            flexWrap: "nowrap",
                            justifyContent:
                                rows.length === 1
                                    ? "flex-start"
                                    : "space-between",
                            columnGap: minGap,
                            width: "100%",
                            minWidth: 0,
                        }}
                    >
                        {row.map((itemIndex) => (
                            <div
                                key={items[itemIndex].key ?? itemIndex}
                                style={{
                                    flex: "0 0 auto",
                                    width: itemWidths[itemIndex] || "max-content",
                                }}
                            >
                                {items[itemIndex]}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ResponsiveChildren;
